// Normalizes visible en/em dashes and the minus sign, and wraps every visible
// "ScopeWorth" text occurrence in <strong class="sw-brand">. Hyphens (- and the
// non-breaking ‑) are left alone, so "client-ready", "read-only" and dates such
// as 2026-10-18 render as written. Runs once on DOM ready, then watches
// for React re-renders via MutationObserver and re-applies as needed.
//
// Skips:
//   - Already-bolded matches (anything inside .sw-brand or [data-sw-brand-skip])
//   - Code/preformatted, form controls, scripts/styles, <title>
//   - Any node inside an SVG for brand wrapping (no <strong> in SVG namespace)
//
// React's diff may clobber our DOM mutations on re-render of a subtree; the
// observer will pick up the new text and re-bold immediately. Net effect for
// the user: brief flicker only on dynamic content, none on static copy.

const BRAND = 'ScopeWorth';
const PATTERN = /ScopeWorth/g;
const DASH_PATTERN = /[–—−]/;

const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'TITLE', 'META', 'LINK',
  'CODE', 'PRE', 'KBD', 'SAMP',
  'INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'BUTTON',
  // BUTTON: we still want the brand bold inside buttons that contain prose,
  // but the existing site uses buttons for short CTAs ("Login", "Download a
  // sample report") that don't include the brand word — safe to skip.
  'STRONG', // already bold; nothing to do
]);

function shouldSkip(textNode) {
  let p = textNode.parentNode;
  while (p && p.nodeType === 1) {
    if (p.namespaceURI && p.namespaceURI !== 'http://www.w3.org/1999/xhtml') return true;
    if (SKIP_TAGS.has(p.tagName)) return true;
    if (p.classList && p.classList.contains('sw-brand')) return true;
    if (p.dataset && p.dataset.swBrandSkip === 'true') return true;
    p = p.parentNode;
  }
  return false;
}

function shouldSkipDash(textNode) {
  let p = textNode.parentNode;
  while (p && p.nodeType === 1) {
    if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'TITLE', 'META', 'LINK', 'INPUT', 'TEXTAREA', 'CODE', 'PRE', 'KBD', 'SAMP'].includes(p.tagName)) return true;
    if (p.dataset && p.dataset.swBrandSkip === 'true') return true;
    p = p.parentNode;
  }
  return false;
}

// A range between digits reads "3 to 5", any other en or em dash becomes a
// comma and the minus sign is spelled out. Hyphens are never touched.
export function normalizeDashCopy(text) {
  if (!text || !DASH_PATTERN.test(text)) return text;
  return text
    .replace(/(\d)\s*[–—]\s*(\d)/g, '$1 to $2')
    .replace(/\s*[–—]+\s*/g, ', ')
    .replace(/^−\s*/, 'minus ')
    .replace(/\s*−\s*/g, ' minus ')
    .replace(/,(\s*,)+/g, ',')
    .replace(/ {2,}/g, ' ');
}

function normalizeDashTextNode(textNode) {
  const text = textNode.nodeValue;
  if (!text || !DASH_PATTERN.test(text) || shouldSkipDash(textNode)) return;
  textNode.nodeValue = normalizeDashCopy(text);
}

function boldifyTextNode(textNode) {
  const text = textNode.nodeValue;
  if (!text) return;
  PATTERN.lastIndex = 0;
  if (!PATTERN.test(text)) return;
  PATTERN.lastIndex = 0;

  const frag = document.createDocumentFragment();
  let lastIndex = 0;
  let match;
  while ((match = PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
    }
    const strong = document.createElement('strong');
    strong.className = 'sw-brand';
    strong.textContent = match[0];
    frag.appendChild(strong);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    frag.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
  if (textNode.parentNode) textNode.parentNode.replaceChild(frag, textNode);
}

function scan(root) {
  if (!root || !root.ownerDocument) return;
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      const hasBrand = node.nodeValue.indexOf(BRAND) !== -1 && !shouldSkip(node);
      const hasDash = DASH_PATTERN.test(node.nodeValue) && !shouldSkipDash(node);
      if (!hasBrand && !hasDash) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const targets = [];
  let n;
  while ((n = walker.nextNode())) targets.push(n);
  for (const t of targets) {
    normalizeDashTextNode(t);
    boldifyTextNode(t);
  }
}

let pending = false;
function scheduleScan() {
  if (pending) return;
  pending = true;
  // Defer to the next frame so React's commit phase finishes first.
  requestAnimationFrame(() => {
    pending = false;
    scan(document.body);
  });
}

function needsScanText(value) {
  return !!value && (value.indexOf(BRAND) !== -1 || DASH_PATTERN.test(value));
}

function start() {
  if (!document.body) return;
  scan(document.body);
  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'characterData') {
        const v = m.target && m.target.nodeValue;
        if (needsScanText(v)) { scheduleScan(); return; }
      } else if (m.type === 'childList') {
        for (const node of m.addedNodes) {
          if (node.nodeType === 3) {
            if (needsScanText(node.nodeValue)) { scheduleScan(); return; }
          } else if (node.nodeType === 1) {
            const el = /** @type {Element} */ (node);
            if (needsScanText(el.textContent)) { scheduleScan(); return; }
          }
        }
      }
    }
  });
  obs.observe(document.body, { childList: true, subtree: true, characterData: true });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
